import { useState, useEffect } from "react";
import { Navigate, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useAdmin } from "@/hooks/useAdmin";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Phone, Mail, ArrowLeft, Loader2, Gift, Instagram, Apple, MessageSquare } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { lovable } from "@/integrations/lovable/index";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { logSmsConsent, SMS_CONSENT_TEXT } from "@/lib/smsConsent";

type AuthStep = "form" | "otp" | "forgot";

const Auth = () => {
  const { user, signIn, signUp } = useAuth();
  const { isAdmin } = useAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const [authSearchParams] = useSearchParams();
  const returnTo = authSearchParams.get("returnTo");
  const [isSignUp, setIsSignUp] = useState(location.pathname === "/signup");
  const [step, setStep] = useState<AuthStep>("form");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [phoneVerificationEnabled, setPhoneVerificationEnabled] = useState(true);
  const [phoneRequired, setPhoneRequired] = useState(true);
  const [emailConfirmationEnabled, setEmailConfirmationEnabled] = useState(true);
  const [showConfirmationMessage, setShowConfirmationMessage] = useState(false);
  const [showNotFoundDialog, setShowNotFoundDialog] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [instagramHandle, setInstagramHandle] = useState("");
  const [smsAlerts, setSmsAlerts] = useState(false);
  const [smsMarketing, setSmsMarketing] = useState(false);
  const [smsMarketingVisible, setSmsMarketingVisible] = useState(false);
  const [unconfirmedEmail, setUnconfirmedEmail] = useState<string | null>(null);
  const [resendingConfirmation, setResendingConfirmation] = useState(false);

  useEffect(() => {
    const checkSettings = async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("key, value")
        .in("key", ["phone_verification_enabled", "phone_required", "email_confirmation_enabled", "sms_marketing_enabled"]);
      if (data) {
        data.forEach((s: any) => {
          if (s.key === "phone_verification_enabled" && s.value === "false") setPhoneVerificationEnabled(false);
          if (s.key === "phone_required" && s.value === "false") setPhoneRequired(false);
          if (s.key === "email_confirmation_enabled" && s.value === "false") setEmailConfirmationEnabled(false);
          if (s.key === "sms_marketing_enabled") setSmsMarketingVisible(s.value === "true");
        });
      }
    };
    checkSettings();
  }, []);

  // Auto-redirect to bookings once email is confirmed and user session is detected
  // This handles both: (1) waiting on confirmation screen, and (2) returning via confirmation link
  useEffect(() => {
    if (user && showConfirmationMessage) {
      toast.success("Email confirmed! Let's book your first appointment.");
      navigate(returnTo || "/book", { replace: true });
    }
  }, [user, showConfirmationMessage, navigate]);

  // Detect if user just confirmed email via the confirmation link (URL contains auth tokens)
  useEffect(() => {
    const hash = window.location.hash;
    if (user && (hash.includes("type=signup") || hash.includes("type=magiclink"))) {
      toast.success("Email confirmed! Welcome to In Paradise Beauty.");
      navigate(returnTo || "/book", { replace: true });
    }
  }, [user, navigate]);

  if (user && !showConfirmationMessage) return <Navigate to={returnTo || (isAdmin ? "/admin" : "/account")} replace />;

  const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const getE164Phone = (formatted: string) => {
    const digits = formatted.replace(/\D/g, "");
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
    return `+${digits}`;
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Check your email for a password reset link!");
      setStep("form");
    }
  };

  const handleSendOtp = async () => {
    if (!phone || phone.replace(/\D/g, "").length < 10) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }
    setOtpSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("phone-otp", {
        body: { phone: getE164Phone(phone), action: "send" },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success("Verification code sent to your phone!");
      setStep("otp");
    } catch (err: any) {
      toast.error(err.message || "Failed to send verification code");
    }
    setOtpSending(false);
  };

  const recordSmsConsents = async (phoneE164: string) => {
    if (!phoneE164) return;
    const { data: { user: newUser } } = await supabase.auth.getUser();
    const uid = newUser?.id ?? null;
    if (smsAlerts) {
      await logSmsConsent({ userId: uid, phone: phoneE164, source: "signup_alerts", consentText: SMS_CONSENT_TEXT.alerts });
    }
    if (smsMarketing && smsMarketingVisible) {
      await logSmsConsent({ userId: uid, phone: phoneE164, source: "signup_marketing", consentText: SMS_CONSENT_TEXT.marketing });
    }
  };

  const handleVerifyAndSignUp = async () => {
    if (otpCode.length !== 6) {
      toast.error("Please enter the 6-digit code");
      return;
    }
    setLoading(true);
    try {
      // Verify OTP
      const { data: verifyData, error: verifyError } = await supabase.functions.invoke("phone-otp", {
        body: { phone: getE164Phone(phone), action: "verify", code: otpCode },
      });
      if (verifyError) throw verifyError;
      if (verifyData?.error) throw new Error(verifyData.error);
      if (!verifyData?.verified) throw new Error("Verification failed");

      // OTP verified — create account
      const { error: signUpError } = await signUp(email, password, fullName, getE164Phone(phone), { referralCode, instagramHandle });
      if (signUpError) throw signUpError;

      await recordSmsConsents(getE164Phone(phone));


      const hasRealEmail = email && !email.includes("@phone.inparadisebeauty");
      if (emailConfirmationEnabled && hasRealEmail) {
        setShowConfirmationMessage(true);
        toast.success("Check your email to confirm your account!");
      } else {
        toast.success("Account created! Welcome to In Paradise Beauty!");
      }
    } catch (err: any) {
      toast.error(err.message || "Verification failed");
    }
    setLoading(false);
  };

  const handleResendConfirmation = async () => {
    if (!unconfirmedEmail) return;
    setResendingConfirmation(true);
    try {
      const { error } = await supabase.functions.invoke("resend-confirmation", {
        body: { email: unconfirmedEmail },
      });
      if (error) throw error;
      toast.success("Confirmation email sent. Please check your inbox.");
    } catch (err: any) {
      toast.error(err.message || "Could not send confirmation email");
    } finally {
      setResendingConfirmation(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setUnconfirmedEmail(null);

    // Allow login with either email or phone-based placeholder email
    let loginEmail = email;
    if (!email.includes("@") && email.replace(/\D/g, "").length >= 10) {
      // User is trying to log in with phone number
      const digits = email.replace(/\D/g, "");
      const e164 = digits.length === 10 ? `1${digits}` : digits;
      loginEmail = `${e164}@phone.inparadisebeauty.local`;
    }

    // Pre-check: pending_clients invites cannot sign in until confirmed.
    if (loginEmail.includes("@") && !loginEmail.endsWith("@phone.inparadisebeauty.local")) {
      const { data: pendingMatch } = await supabase
        .from("pending_clients")
        .select("id")
        .ilike("email", loginEmail)
        .is("confirmed_at", null)
        .maybeSingle();
      if (pendingMatch) {
        setLoading(false);
        setUnconfirmedEmail(loginEmail);
        toast.error("Please confirm your account from the email we sent you.");
        return;
      }
    }

    const { error } = await signIn(loginEmail, password);
    setLoading(false);
    if (error) {
      const msg = (error.message || "").toLowerCase();
      if (msg.includes("email not confirmed") || msg.includes("not confirmed")) {
        setUnconfirmedEmail(loginEmail);
        toast.error("Please confirm your email address before signing in.");
        return;
      }
      if (msg.includes("invalid login credentials")) {
        // Use security-definer RPC to check account existence (bypasses RLS)
        const { data: emailExists } = await supabase.rpc("check_email_exists", { _email: loginEmail });

        if (emailExists) {
          toast.error("Incorrect password. Please try again or reset your password.");
        } else {
          setShowNotFoundDialog(true);
        }
      } else {
        toast.error(error.message);
      }
    } else {
      toast.success("Welcome back!");
    }
  };

  const handleSignUpStart = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim()) {
      toast.error("Please enter your full name");
      return;
    }
    const phoneDigits = phone.replace(/\D/g, "");
    const hasPhone = phoneDigits.length >= 10;
    if (phoneDigits.length < 10) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }
    const emailTrim = email.trim().toLowerCase();
    const emailShapeOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim);
    const isPlaceholderEmail = emailTrim.includes("@phone.inparadisebeauty");
    if (!emailTrim) {
      toast.error("A real email address is required — even if you signed up with a phone number. Please enter the email you'd like to use for booking confirmations and receipts.");
      return;
    }
    if (!emailShapeOk || isPlaceholderEmail) {
      toast.error("Please enter a valid email address (e.g. you@example.com). A phone number alone isn't enough to create your account.");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    // Skip OTP if verification disabled, OR if phone is optional and not provided
    if (!phoneVerificationEnabled || !hasPhone) {
      setLoading(true);
      try {
        const phoneToUse = hasPhone ? getE164Phone(phone) : "";
        const { error: signUpError } = await signUp(email, password, fullName, phoneToUse, { referralCode, instagramHandle });
        if (signUpError) throw signUpError;
        if (phoneToUse) await recordSmsConsents(phoneToUse);
        const hasRealEmail = email && !email.includes("@phone.inparadisebeauty");
        if (emailConfirmationEnabled && hasRealEmail) {
          setShowConfirmationMessage(true);
          toast.success("Check your email to confirm your account!");
        } else {
          toast.success("Account created! Welcome to In Paradise Beauty!");
        }
      } catch (err: any) {
        toast.error(err.message || "Sign up failed");
      }
      setLoading(false);
      return;
    }
    // Send OTP to verify phone
    await handleSendOtp();
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="pt-32 pb-24">
        <div className="container mx-auto px-6 max-w-md">
          <div className="text-center mb-10">
            <p className="text-primary text-sm tracking-[0.3em] uppercase mb-3">
              {showConfirmationMessage ? "Check Your Email" : step === "forgot" ? "Reset Password" : isSignUp ? "Create Account" : "Welcome Back"}
            </p>
            <h1 className="font-serif text-4xl md:text-5xl font-semibold">
              {showConfirmationMessage ? "Confirm Email" : step === "forgot" ? "Forgot Password" : step === "otp" ? "Verify Phone" : isSignUp ? "Sign Up" : "Sign In"}
            </h1>
          </div>

          {showConfirmationMessage && (
            <div className="space-y-6 text-center">
              <div className="bg-card border border-border/50 p-8 space-y-4">
                <Mail size={40} className="mx-auto text-primary" />
                <h2 className="font-serif text-2xl font-semibold">Almost There!</h2>
                <p className="text-muted-foreground text-sm">We sent a confirmation link to</p>
                <p className="font-semibold text-lg text-primary">{email || "your email"}</p>
              </div>
              <div className="bg-muted/50 border border-border/30 p-5 space-y-2">
                <p className="text-foreground text-sm font-medium">
                  ✨ Confirm your email to start scheduling appointments
                </p>
                <p className="text-muted-foreground text-xs">
                  Click the link in your inbox, then come back here to sign in and book your first beauty appointment.
                </p>
              </div>
              <p className="text-muted-foreground text-xs">
                Didn't receive it? Check your spam folder or try signing up again.
              </p>
              <Button
                onClick={() => {
                  setShowConfirmationMessage(false);
                  setIsSignUp(false);
                  setStep("form");
                }}
                variant="outline"
                className="w-full rounded-full font-semibold tracking-wider uppercase"
              >
                Go to Sign In
              </Button>
            </div>
          )}

          {step === "forgot" && (
            <form onSubmit={handleForgotPassword} className="space-y-5">
              <p className="text-muted-foreground text-sm text-center">Enter your email and we'll send you a link to reset your password.</p>
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email</Label>
                <Input id="reset-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required className="bg-card border-border" />
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-full font-semibold tracking-wider uppercase">
                {loading ? "Please wait..." : "Send Reset Link"}
              </Button>
              <p className="text-center text-muted-foreground text-sm">
                <button type="button" onClick={() => setStep("form")} className="text-primary hover:underline font-medium">Back to Sign In</button>
              </p>
            </form>
          )}

          {step === "otp" && (
            <div className="space-y-6 text-center">
              <div className="bg-card border border-border/50 p-6">
                <Phone size={32} className="mx-auto text-primary mb-3" />
                <p className="text-muted-foreground text-sm mb-1">We sent a 6-digit code to</p>
                <p className="font-semibold text-lg">{phone}</p>
              </div>

              <div className="flex justify-center">
                <InputOTP maxLength={6} value={otpCode} onChange={setOtpCode}>
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button
                onClick={handleVerifyAndSignUp}
                disabled={loading || otpCode.length !== 6}
                className="w-full rounded-full font-semibold tracking-wider uppercase"
              >
                {loading ? (
                  <><Loader2 size={16} className="animate-spin mr-2" /> Verifying...</>
                ) : (
                  "Verify & Create Account"
                )}
              </Button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => { setStep("form"); setOtpCode(""); }}
                  className="text-primary hover:underline font-medium flex items-center gap-1"
                >
                  <ArrowLeft size={14} /> Back
                </button>
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={otpSending}
                  className="text-primary hover:underline font-medium"
                >
                  {otpSending ? "Sending..." : "Resend Code"}
                </button>
              </div>
            </div>
          )}

          {step === "form" && !isSignUp && (
            <form onSubmit={handleSignIn} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="login-email">Email or Phone</Label>
                <Input
                  id="login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com or (555) 123-4567"
                  required
                  className="bg-card border-border"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-password">Password</Label>
                <Input
                  id="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="bg-card border-border"
                />
              </div>
              <div className="text-right">
                <button type="button" onClick={() => setStep("forgot")} className="text-primary hover:underline text-sm font-medium">
                  Forgot password?
                </button>
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-full font-semibold tracking-wider uppercase">
                {loading ? "Please wait..." : "Sign In"}
              </Button>

              {unconfirmedEmail && (
                <div className="border border-primary/30 bg-primary/5 p-4 rounded-sm space-y-3">
                  <p className="text-sm text-foreground">
                    Your account hasn't been confirmed yet. Please check your inbox for the confirmation email we sent to <span className="font-medium">{unconfirmedEmail}</span>.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleResendConfirmation}
                    disabled={resendingConfirmation}
                    className="w-full"
                  >
                    {resendingConfirmation ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...</>
                    ) : (
                      "Resend confirmation email"
                    )}
                  </Button>
                </div>
              )}
            </form>
          )}

          {step === "form" && isSignUp && (
            <form onSubmit={handleSignUpStart} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="signup-name">Full Name <span className="text-destructive">*</span></Label>
                <Input
                  id="signup-name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Your full name"
                  required
                  className="bg-card border-border"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="signup-phone" className="flex items-center gap-1.5">
                  <Phone size={14} /> Phone Number <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="signup-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                  placeholder="(555) 123-4567"
                  required
                  maxLength={14}
                  className="bg-card border-border"
                />
                <p className="text-xs text-muted-foreground">
                  {phoneVerificationEnabled ? "We'll send a verification code to this number" : "Required for booking notifications"}
                </p>

                <div className="mt-3 space-y-3 border border-border/50 bg-card/50 p-4 rounded-sm">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="sms-alerts"
                      checked={smsAlerts}
                      onCheckedChange={(checked) => setSmsAlerts(checked === true)}
                      className="mt-0.5"
                    />
                    <div className="space-y-1">
                      <label htmlFor="sms-alerts" className="text-sm font-medium text-foreground cursor-pointer flex items-center gap-1.5">
                        <MessageSquare size={14} className="text-primary" /> Text me with alerts
                      </label>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        By checking this box, you agree to receive recurring SMS appointment confirmations, reminders, receipts, and review requests from In Paradise Beauty at the phone number provided (up to ~5 messages per appointment). Msg & data rates may apply. Reply <strong className="text-foreground">STOP</strong> to opt out, <strong className="text-foreground">HELP</strong> for help. See the <a href="/sms-consent" target="_blank" className="text-primary underline">SMS terms</a> and <a href="/privacy-policy" target="_blank" className="text-primary underline">Privacy Policy</a>.
                      </p>
                    </div>
                  </div>

                  {smsMarketingVisible && (
                    <>
                      <div className="border-t border-border/30" />

                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="sms-marketing"
                          checked={smsMarketing}
                          onCheckedChange={(checked) => setSmsMarketing(checked === true)}
                          className="mt-0.5"
                        />
                        <div className="space-y-1">
                          <label htmlFor="sms-marketing" className="text-sm font-medium text-foreground cursor-pointer flex items-center gap-1.5">
                            <MessageSquare size={14} className="text-primary" /> Text me with marketing messages
                          </label>
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            By checking this box, you agree to receive recurring marketing and promotional SMS messages from In Paradise Beauty at the phone number provided (up to ~4 messages per month). Msg & data rates may apply. Consent is not a condition of purchase. Reply <strong className="text-foreground">STOP</strong> to opt out, <strong className="text-foreground">HELP</strong> for help. See the <a href="/sms-consent" target="_blank" className="text-primary underline">SMS terms</a>.
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="signup-email" className="flex items-center gap-1.5">
                  <Mail size={14} /> Email <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="signup-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="bg-card border-border"
                />
                <p className="text-xs text-muted-foreground">
                  A real email is required — even if you also provide a phone number. We use it for booking confirmations, receipts, and account recovery.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="signup-password">Password <span className="text-destructive">*</span></Label>
                <Input
                  id="signup-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="bg-card border-border"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="signup-referral" className="flex items-center gap-1.5">
                  <Gift size={14} /> Referral Code <span className="text-muted-foreground text-xs">(optional)</span>
                </Label>
                <Input
                  id="signup-referral"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  placeholder="e.g. A1B2C3D4"
                  className="bg-card border-border font-mono tracking-widest uppercase"
                  maxLength={10}
                />
                <p className="text-xs text-muted-foreground">
                  Have a friend's referral code? Enter it to get a discount on your first booking
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="signup-instagram" className="flex items-center gap-1.5">
                  <Instagram size={14} /> Instagram <span className="text-muted-foreground text-xs">(optional)</span>
                </Label>
                <Input
                  id="signup-instagram"
                  value={instagramHandle}
                  onChange={(e) => setInstagramHandle(e.target.value)}
                  placeholder="@yourusername"
                  className="bg-card border-border"
                />
              </div>
              <Button type="submit" disabled={otpSending || loading} className="w-full rounded-full font-semibold tracking-wider uppercase">
                {otpSending ? (
                  <><Loader2 size={16} className="animate-spin mr-2" /> Sending Code...</>
                ) : loading ? (
                  <><Loader2 size={16} className="animate-spin mr-2" /> Creating Account...</>
                ) : phoneVerificationEnabled ? (
                  "Continue"
                ) : (
                  "Create Account"
                )}
              </Button>
            </form>
          )}

          {step === "form" && !showConfirmationMessage && (
            <div className="mt-6 space-y-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full rounded-full font-semibold tracking-wider"
                onClick={async () => {
                  const { error } = await lovable.auth.signInWithOAuth("google", {
                    redirect_uri: window.location.origin,
                  });
                  if (error) toast.error(error.message || "Google sign-in failed");
                }}
              >
                <svg className="mr-2" width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                Sign in with Google
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full rounded-full font-semibold tracking-wider"
                onClick={async () => {
                  const { error } = await lovable.auth.signInWithOAuth("apple", {
                    redirect_uri: window.location.origin,
                  });
                  if (error) toast.error(error.message || "Apple sign-in failed");
                }}
              >
                <Apple size={18} className="mr-2" />
                Sign in with Apple
              </Button>
            </div>
          )}

          {step === "form" && (
            <p className="text-center text-muted-foreground text-sm mt-8">
              {isSignUp ? "Already have an account?" : "Don't have an account?"}{" "}
              <button
                onClick={() => { setIsSignUp(!isSignUp); setStep("form"); }}
                className="text-primary hover:underline font-medium"
              >
                {isSignUp ? "Sign In" : "Sign Up"}
              </button>
            </p>
          )}
        </div>
      </section>
      <Footer />

      <Dialog open={showNotFoundDialog} onOpenChange={setShowNotFoundDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl italic">Account Not Found</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              This username does not exist in the system. Would you like to create an account?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowNotFoundDialog(false)} className="rounded-none border-border/50 text-xs tracking-widest uppercase">
              Cancel
            </Button>
            <Button onClick={() => { setShowNotFoundDialog(false); setIsSignUp(true); }} className="rounded-none text-xs tracking-widest uppercase">
              Sign Up
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Auth;
